import { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Progress, Spin, Empty, message, Segmented, Select, Space } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { charging, dashboardApi } from '../../../services/api';

const STATUS_COLOR = { '充电': 'volcano', '已插枪': 'gold', '空闲': 'green', '故障': 'red', '离线': 'default' };
const STATUS_OPTIONS = ['充电', '已插枪', '空闲', '故障', '离线'];

export default function Charging() {
  const [loading, setLoading] = useState(true);
  const [piles, setPiles] = useState([]);        // /status 原始桩级数据（含 guns 枪级数组）
  const [loadCurve, setLoadCurve] = useState(null);
  const [viewMode, setViewMode] = useState('gun'); // gun=按枪显示 / pile=按桩聚合
  const [stationFilter, setStationFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  // 分页受控：8 秒轮询刷新 dataSource 时保持当前页不被重置
  const [gunPage, setGunPage] = useState({ current: 1, pageSize: 20 });

  const inFlightRef = useRef(false);

  const loadData = async () => {
    // 轮询竞态守卫：上一轮未返回时跳过本轮，避免慢请求重叠乱序
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const [res, trendRes] = await Promise.all([
        charging.status(),
        dashboardApi.trend({ hours: 24 }),
      ]);
      setPiles(res.data || []);
      // 充电网络总负荷曲线（全部充电表计按小时聚合，不再是单台桩）
      setLoadCurve(trendRes.data || null);
    } catch (err) {
      message.error(err.message || '加载充电桩数据失败');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const timer = setInterval(loadData, 8000);
    return () => clearInterval(timer);
  }, []);

  // 站点选项：取 /status 返回的 station_name 实算，不写死
  const stationOptions = [...new Set(piles.map(p => p.station_name).filter(Boolean))];

  // 站点过滤后的桩
  const filteredPiles = stationFilter ? piles.filter(p => p.station_name === stationFilter) : piles;

  // 枪级行：每把枪一行；无实时数据的枪（no_data）如实显示 "-"，不编数
  const gunRows = filteredPiles.flatMap(p => (p.guns || []).map(g => ({
    key: `${p.id}-${g.gun_no}`,
    pile_name: p.name,
    gun_no: g.gun_no,
    type: p.type,
    status: g.status,
    current_power: g.current_power,
    soc: g.current_soc,
    energy: g.energy_session,
    duration: g.charging_duration,
    data_source: g.data_source,
  })));
  const filteredGunRows = statusFilter ? gunRows.filter(g => g.status === statusFilter) : gunRows;

  // 桩级行（保持原表格口径）
  const pileRows = filteredPiles.map(item => ({
    key: item.id,
    name: item.name,
    type: item.type,
    rated_power: parseFloat(item.rated_power),
    status: item.status,
    current_power: item.current_power,
    power_source: item.power_source,
    soc: item.current_soc,
    energy: item.energy_delivered,
    duration: item.charging_duration,
  }));
  const filteredPileRows = statusFilter ? pileRows.filter(p => p.status === statusFilter) : pileRows;

  // 顶部统计：桩数/枪数都从 /status 实算
  // 使用中 = 充电中 + 已插枪（枪被占用即算使用；此前按"占用"匹配，与平台状态值不符恒为 0）
  const allGuns = piles.flatMap(p => p.guns || []);
  const gunTotal = allGuns.length;
  const gunsInUse = allGuns.filter(g => g.status === '充电' || g.status === '已插枪').length;
  // 总负荷：优先枪级功率求和；一把枪都没建档时回退桩级功率求和
  const currentLoad = gunTotal > 0
    ? allGuns.reduce((s, g) => s + (typeof g.current_power === 'number' ? g.current_power : 0), 0)
    : piles.reduce((s, p) => s + (p.current_power || 0), 0);

  const gunColumns = [
    { title: '所属桩', dataIndex: 'pile_name' },
    { title: '枪口', dataIndex: 'gun_no', render: v => `枪 ${v}`, sorter: (a, b) => a.gun_no - b.gun_no },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag>,
      sorter: (a, b) => String(a.type || '￿').localeCompare(String(b.type || '￿'), 'zh') },
    { title: '状态', dataIndex: 'status', render: (v, row) => row.data_source === 'no_data'
      ? '-' : <Tag color={STATUS_COLOR[v] || 'default'}>{v || '未知'}</Tag>,
      // 无数据(null)排最后：用 \uffff 兜底，轮询刷新不改变排序
      sorter: (a, b) => String(a.status || '￿').localeCompare(String(b.status || '￿'), 'zh') },
    { title: '当前功率(kW)', dataIndex: 'current_power', render: v => typeof v === 'number' ? v.toFixed(1) : '-',
      sorter: (a, b) => (typeof a.current_power === 'number' ? a.current_power : -1) - (typeof b.current_power === 'number' ? b.current_power : -1) },
    { title: 'SOC(%)', dataIndex: 'soc', render: v => typeof v === 'number' ? <Progress percent={v} size="small" /> : '-' },
    { title: '已充电量(kWh)', dataIndex: 'energy', render: v => typeof v === 'number' ? v.toFixed(1) : '-' },
    { title: '充电时长(分钟)', dataIndex: 'duration', render: v => typeof v === 'number' ? v : '-' },
  ];

  const pileColumns = [
    { title: '桩名称', dataIndex: 'name' },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
    { title: '额定功率(kW)', dataIndex: 'rated_power', render: v => Number.isFinite(v) ? v : '-' },
    { title: '状态', dataIndex: 'status', render: v => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag> },
    { title: '当前功率(kW)', dataIndex: 'current_power', render: (v, row) => v == null ? '-' : (
      <span>{v.toFixed(1)}{(row.power_source === 'estimated' || row.power_source === 'carried' || row.power_source === 'vi_calc') &&
        <Tag color="orange" style={{ marginLeft: 4, fontSize: 11 }}>估算</Tag>}</span>
    ) },
    { title: 'SOC(%)', dataIndex: 'soc', render: v => v ? <Progress percent={v} size="small" /> : '-' },
    { title: '已充电量(kWh)', dataIndex: 'energy', render: v => v?.toFixed?.(1) || '-' },
    { title: '充电时长', dataIndex: 'duration', render: v => v || '-' },
  ];

  const curveLabels = loadCurve?.labels || [];
  const chargingSeries = loadCurve?.series?.charging || [];
  const hasCurve = chargingSeries.some(v => v != null);

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['充电功率'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: curveLabels },
    yAxis: [{ type: 'value', name: 'kW' }],
    series: [
      { name: '充电功率', type: 'line', smooth: true, connectNulls: true, data: chargingSeries, itemStyle: { color: '#1890ff' } },
    ]
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="充电桩" value={piles.length} suffix="台" /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="充电枪" value={gunTotal} suffix="把" /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="使用中" value={gunsInUse} suffix="把" valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="当前总负荷" value={currentLoad} suffix="kW" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}><Card title="充电网络总负荷曲线 (24h)" size="small">
          {hasCurve
            ? <ReactEChartsCore option={chartOption} style={{ height: 260 }} />
            : <Empty description="暂无历史遥测数据" style={{ padding: '50px 0' }} />}
        </Card></Col>
      </Row>
      <Card
        title={viewMode === 'gun' ? '充电枪实时状态' : '充电桩实时状态'}
        style={{ marginTop: 16 }}
        size="small"
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="站点（全部）"
              style={{ minWidth: 220 }}
              value={stationFilter}
              onChange={v => { setStationFilter(v); setGunPage(p => ({ ...p, current: 1 })); }}
              options={stationOptions.map(s => ({ value: s, label: s }))}
            />
            <Select
              allowClear
              placeholder="状态（全部）"
              style={{ minWidth: 130 }}
              value={statusFilter}
              onChange={v => { setStatusFilter(v); setGunPage(p => ({ ...p, current: 1 })); }}
              options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))}
            />
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'gun', label: '按枪显示' },
                { value: 'pile', label: '按桩聚合' },
              ]}
            />
          </Space>
        }
      >
        {viewMode === 'gun' ? (
          <Table
            columns={gunColumns}
            dataSource={filteredGunRows}
            size="small"
            pagination={{
              current: gunPage.current,
              pageSize: gunPage.pageSize,
              onChange: (current, pageSize) => setGunPage({ current, pageSize }),
              showSizeChanger: true,
              pageSizeOptions: ['20', '50'],
              showTotal: t => `共 ${t} 把枪`,
            }}
          />
        ) : (
          <Table columns={pileColumns} dataSource={filteredPileRows} pagination={false} size="small" />
        )}
      </Card>
    </div>
  );
}
